import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { DEFAULT_LETTERHEAD, LetterheadConfig } from '../data/defaultLetterhead';
import { DEFAULT_TYPOGRAPHY, TypographyStyle } from '../data/typographyTemplates';
import { useDocumentStorage, DocumentState } from '../hooks/useDocumentStorage';
import { DEFAULT_MARKDOWN_CONTENT } from '../data/defaultContent';

// Re-export types for external use
export type { LetterheadConfig, TypographyStyle };

interface DocumentContextValue {
  content: string;
  letterheadConfig: LetterheadConfig;
  typographyStyle: TypographyStyle;
  lastSaved: string | null;
  isDirty: boolean;
  setContent: (content: string) => void;
  setLetterheadConfig: (config: Partial<LetterheadConfig>) => void;
  setTypographyStyle: (style: TypographyStyle) => void;
  updateTypographyStyle: (updates: Partial<TypographyStyle>) => void;
  toggleLetterhead: () => void;
  clearDocument: () => void;
}

const DocumentContext = createContext<DocumentContextValue | undefined>(undefined);

export const DocumentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { loadDraft, saveDraft, clearDraft } = useDocumentStorage();

  // Initialize state from localStorage or defaults
  const [content, setContentState] = useState<string>(() => {
    const draft = loadDraft();
    return draft?.content || DEFAULT_MARKDOWN_CONTENT;
  });

  const [letterheadConfig, setLetterheadConfigState] = useState<LetterheadConfig>(() => {
    const draft = loadDraft();
    // Merge with defaults to handle old data without new properties
    return draft?.letterheadConfig
      ? { ...DEFAULT_LETTERHEAD, ...draft.letterheadConfig }
      : DEFAULT_LETTERHEAD;
  });

  const [typographyStyle, setTypographyStyleState] = useState<TypographyStyle>(() => {
    const draft = loadDraft();
    // Merge with defaults to handle old data without new properties
    return draft?.typographyStyle
      ? { ...DEFAULT_TYPOGRAPHY, ...draft.typographyStyle }
      : DEFAULT_TYPOGRAPHY;
  });

  const [lastSaved, setLastSaved] = useState<string | null>(() => {
    const draft = loadDraft();
    return draft?.lastSaved || null;
  });

  const [isDirty, setIsDirty] = useState(false);

  // Auto-save when content, letterhead, or typography changes
  useEffect(() => {
    if (!isDirty) {
      setIsDirty(true);
      return;
    }

    const timer = setTimeout(() => {
      try {
        saveDraft({ content, letterheadConfig, typographyStyle });
        setLastSaved(new Date().toISOString());
        setIsDirty(false);
      } catch (error) {
        console.error('Auto-save failed:', error);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [content, letterheadConfig, typographyStyle]);

  const setContent = useCallback((newContent: string) => {
    setContentState(newContent);
    setIsDirty(true);
  }, []);

  const setLetterheadConfig = useCallback((config: Partial<LetterheadConfig>) => {
    setLetterheadConfigState((prev) => ({ ...prev, ...config }));
    setIsDirty(true);
  }, []);

  const setTypographyStyle = useCallback((style: TypographyStyle) => {
    setTypographyStyleState(style);
    setIsDirty(true);
  }, []);

  const updateTypographyStyle = useCallback((updates: Partial<TypographyStyle>) => {
    setTypographyStyleState((prev) => ({ ...prev, ...updates }));
    setIsDirty(true);
  }, []);

  const toggleLetterhead = useCallback(() => {
    setLetterheadConfigState((prev) => ({ ...prev, enabled: !prev.enabled }));
    setIsDirty(true);
  }, []);

  const clearDocument = useCallback(() => {
    setContentState(DEFAULT_MARKDOWN_CONTENT);
    setLetterheadConfigState(DEFAULT_LETTERHEAD);
    setTypographyStyleState(DEFAULT_TYPOGRAPHY);
    clearDraft();
    setLastSaved(null);
    setIsDirty(false);
  }, [clearDraft]);

  const value: DocumentContextValue = {
    content,
    letterheadConfig,
    typographyStyle,
    lastSaved,
    isDirty,
    setContent,
    setLetterheadConfig,
    setTypographyStyle,
    updateTypographyStyle,
    toggleLetterhead,
    clearDocument,
  };

  return <DocumentContext.Provider value={value}>{children}</DocumentContext.Provider>;
};

export const useDocument = (): DocumentContextValue => {
  const context = useContext(DocumentContext);
  if (!context) {
    throw new Error('useDocument must be used within DocumentProvider');
  }
  return context;
};
